<template>
  <div class="text-xs">
    <div class="flex items-center gap-2 mb-1">
      <div class="relative flex-1 max-w-2xl">
        <input
          ref="input"
          v-model="term"
          type="text"
          placeholder="MBI, member ID, MRN, EHR ID (CLIENT|CHART), or name (Smith, Jane)"
          class="sensitive w-full border border-gray-200 rounded pl-8 pr-3 py-2 focus:outline-none focus:border-gray-400"
          @keyup.enter="search"
        />
        <font-awesome-icon
          :icon="['fas', 'magnifying-glass']"
          class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
        />
      </div>
      <button
        type="button"
        class="btn-primary-sm"
        :disabled="term.trim().length < 2 || loading"
        @click="search"
      >Search</button>
    </div>

    <!-- Say what WILL be searched before it runs. An identifier matches
         exactly and a name matches as a prefix; without this the user cannot
         tell why 'SMIT' found people and a half-typed member id found none. -->
    <div class="text-gray-500 mb-3 h-4">
      <template v-if="term.trim().length >= 2">
        {{ hint }}
      </template>
    </div>

    <LoadingIndicator v-if="loading" message="Searching..." />

    <div v-else-if="error" class="text-red-600">
      {{ error }}
    </div>

    <div v-else-if="searched && !results.length" class="text-gray-500">
      No match for "{{ lastTerm }}".
      <template v-if="lastKind !== 'name'">
        Identifiers must match exactly — partial IDs will not match.
      </template>
      <template v-else>
        Names match from the start; try just the surname.
      </template>
    </div>

    <template v-else-if="results.length">
      <div class="text-gray-500 mb-1.5">
        {{ results.length }}{{ results.length === limit ? '+' : '' }} match{{ results.length === 1 ? '' : 'es' }}
      </div>
      <div class="border border-gray-200 rounded overflow-auto max-h-[560px]">
        <table class="w-full">
          <colgroup>
            <col style="width: 24%" /><col style="width: 11%" />
            <col style="width: 7%" /><col style="width: 12%" />
            <col style="width: 9%" /><col style="width: 25%" />
            <col style="width: 12%" />
          </colgroup>
          <thead class="sticky top-0 bg-white shadow-sm">
            <tr class="text-left font-medium uppercase text-gray-500 border-b border-gray-200">
              <th class="py-2 pl-3 pr-2">Name</th>
              <th class="py-2 px-2">DOB</th>
              <th class="py-2 px-2">Sex</th>
              <th class="py-2 px-2">Location</th>
              <th class="py-2 px-2 text-right">Clusters</th>
              <th class="py-2 px-2">Matched on</th>
              <th class="py-2 px-2 pr-3"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="r in results"
              :key="r.PATIENT_MASTER"
              class="border-b border-gray-50 hover:bg-gray-50 cursor-pointer"
              @click="$emit('open', r.PATIENT_MASTER)"
            >
              <td class="py-1.5 pl-3 pr-2 align-middle sensitive">{{ name(r) }}</td>
              <td class="py-1.5 px-2 align-middle text-gray-600 sensitive">{{ r.DOB || '—' }}</td>
              <td class="py-1.5 px-2 align-middle text-gray-600">{{ sexLabel(r.SEX) }}</td>
              <td class="py-1.5 px-2 align-middle text-gray-600">
                {{ [r.STATE, r.ZIP].filter(Boolean).join(' ') || '—' }}
              </td>
              <td class="py-1.5 px-2 align-middle text-right text-gray-600">
                {{ r.N_ID_CLUSTERS == null ? '—' : r.N_ID_CLUSTERS }}
              </td>
              <td class="py-1.5 px-2 align-middle text-gray-500">
                <template v-if="r.MATCHED_ID_TYPE">
                  {{ r.MATCHED_ID_TYPE }}: {{ r.MATCHED_ID_VALUE }}
                </template>
                <span v-else>name</span>
              </td>
              <td class="py-1.5 px-2 pr-3 align-middle text-right">
                <span class="text-blue-600">View &rarr;</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <div v-else class="text-gray-500">
      Search resolves any identifier to the person holding it, across every feed.
      Because the full identifier set is searched, a SUPERSEDED MBI still finds
      the patient — which is the case a crosswalk lookup misses.
    </div>
  </div>
</template>

<script>
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import { searchPatients } from '@/services/api/mdr/read';

const LIMIT = 100;

export default {
  name: 'MDRSearch',
  components: { LoadingIndicator },
  emits: ['open'],
  data() {
    return {
      term: '',
      lastTerm: '',
      lastKind: null,
      results: [],
      loading: false,
      error: null,
      searched: false,
      limit: LIMIT,
    };
  },
  computed: {
    // Mirrors _classify_term() on the backend. Duplicated deliberately: this
    // is a pre-flight hint typed into an input, and a round trip per keystroke
    // to be told the shape of your own text is not worth it. The backend
    // classification is the one that decides the query.
    kind() {
      const t = this.term.trim();
      if (!t) return 'empty';
      if (t.includes('|')) return 'composite';
      if (t.length === 36 && (t.match(/-/g) || []).length === 4) return 'uuid';
      if (t.length === 11 && /^\d/.test(t) && /^[a-zA-Z0-9]+$/.test(t) && !/^\d+$/.test(t)) return 'mbi';
      if (/^[\d-]+$/.test(t)) return 'numeric';
      return 'name';
    },
    hint() {
      return {
        mbi: 'Looks like an MBI — exact match across all identifiers.',
        composite: 'Composite EHR ID (CLIENT|CHART) — exact match.',
        numeric: 'Numeric ID (member ID, MRN, NPI, TIN) — exact match.',
        uuid: 'UUID — exact match.',
        name: 'Name — prefix match. Use "Last, First" to narrow.',
      }[this.kind] || '';
    },
  },
  mounted() {
    this.$refs.input?.focus();
  },
  methods: {
    name(r) {
      const parts = [r.FIRST_NAME, r.MIDDLE_NAME, r.LAST_NAME].filter(Boolean);
      return parts.length ? parts.join(' ') : '(no name)';
    },
    sexLabel(s) {
      return { 1: 'M', 2: 'F' }[String(s)] || '—';
    },
    async search() {
      const t = this.term.trim();
      if (t.length < 2) return;
      this.loading = true;
      this.error = null;
      try {
        const data = await searchPatients(t, LIMIT);
        this.results = data.results || [];
        this.lastTerm = t;
        this.lastKind = data.kind;
        this.searched = true;
      } catch (e) {
        this.error = e?.response?.status === 403
          ? 'Patient search requires PHI access (data_phi).'
          : "Search failed.";
        this.results = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
